import React, { useState, useMemo } from 'react';
import UsePayments from './UsePayments';
import './Style/AllPayments.css';

const toIST = (dateString) => {
    if (!dateString) return 'Loading...';
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;

    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    return istDate.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};


const AllPayments = () => {
    const { allPayments, loading } = UsePayments();
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Calculate statistics
    const stats = useMemo(() => {
        const totalAmount = allPayments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
        const totalTransactions = allPayments.length;
        
        return {
            totalAmount: totalAmount.toFixed(2),
            totalTransactions,
            successfulPayments: totalTransactions // Assuming all are successful for now
        };
    }, [allPayments]);

    // Filter payments based on search term and date range
    const filteredPayments = useMemo(() => {
        let filtered = allPayments;

        if (searchTerm) {
            const lowercasedSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(payment => 
                payment.name.toLowerCase().includes(lowercasedSearch) ||
                payment.phone.includes(searchTerm) ||
                payment.paymentId.toLowerCase().includes(lowercasedSearch) ||
                payment.buyed.toLowerCase().includes(lowercasedSearch)
            );
        }

        if (startDate) {
            filtered = filtered.filter(payment => 
                new Date(payment.date) >= new Date(startDate)
            );
        }

        if (endDate) {
            filtered = filtered.filter(payment => 
                new Date(payment.date) <= new Date(endDate)
            );
        }

        return filtered;
    }, [allPayments, searchTerm, startDate, endDate]);

    // Export to CSV function
    const exportToCSV = () => {
        const headers = ['ID', 'Name', 'Phone', 'Amount', 'Product', 'Payment ID', 'Date', 'Coupon'];
        const csvData = filteredPayments.map(payment => [
            payment.id,
            payment.name,
            payment.phone,
            payment.amount,
            payment.buyed,
            payment.paymentId,
            payment.date,
            payment.coupon || 'N/A'
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.map(field => `"${field}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payments-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <div className="loading-text">Loading payments...</div>
            </div>
        );
    }

    if (!allPayments || allPayments.length === 0) {
        return (
            <div className="empty-state">
                <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <h3>No payments found</h3>
                <p>There are no payment records in the system yet.</p>
            </div>
        );
    }

    return (
        <div className="payments-container">
            <header className="payments-header">
                <h2>Payment Management</h2>
                <div className="payments-stats">
                    <div className="stat-card total-amount">
                        <div className="stat-label">Total Amount</div>
                        <div className="stat-value amount">₹{stats.totalAmount}</div>
                    </div>
                    <div className="stat-card total-transactions">
                        <div className="stat-label">Total Transactions</div>
                        <div className="stat-value">{stats.totalTransactions}</div>
                    </div>
                    <div className="stat-card successful">
                        <div className="stat-label">Successful</div>
                        <div className="stat-value">{stats.successfulPayments}</div>
                    </div>
                </div>
            </header>

            <div className="payments-controls">
                <div className="search-box">
                    <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.3-4.3"></path>
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by name, phone, payment ID, or product..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filter-controls">
                    <div className="date-filter">
                        <input
                            type="date"
                            className="date-input"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            placeholder="From Date"
                        />
                        <span>to</span>
                        <input
                            type="date"
                            className="date-input"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            placeholder="To Date"
                        />
                    </div>
                    <button className="export-btn" onClick={exportToCSV}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Export CSV
                    </button>
                </div>
            </div>

            <div className="table-container">
                <table className="payments-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Email</th>
                            <th>Amount</th>
                            <th>Product</th>
                            <th>Payment ID</th>
                            <th>Date</th>
                            <th>Year</th>
                            <th>Coupon</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPayments.slice().reverse().map((payment) => (
                            <tr key={payment.id}>
                                <td>{payment.id}</td>
                                <td>{payment.name}</td>
                                <td>{payment.phone}</td>
                                <td>{payment.email}</td>
                                <td>₹{payment.amount}</td>
                                <td>{payment.buyed}</td>
                                <td>{payment.paymentId}</td>
                                <td>{toIST(payment.date)}</td>
                                <td>{payment.year}</td>
                                <td>{payment.coupon || ''}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AllPayments;